import { css, useTheme } from '@emotion/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

interface UserActionsMenuProps {
  subscriptionStatus: string | null;
  onChangePlan: () => void;
  onChangeStatus: (action: 'activate' | 'cancel' | 'disable') => void;
  onDelete: () => void;
}

export function UserActionsMenu({
  subscriptionStatus,
  onChangePlan,
  onChangeStatus,
  onDelete,
}: UserActionsMenuProps) {
  const theme = useTheme();

  const itemCss = css`
    padding: ${spacing.sm} ${spacing.md};
    font-size: ${fontSizes.sm};
    color: ${theme.colors.text};
    cursor: pointer;
    border-radius: 4px;
    outline: none;
    &[data-highlighted] {
      background: ${theme.colors.surface};
    }
  `;

  const destructiveItemCss = css`
    ${itemCss};
    color: ${theme.colors.error};
  `;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label="Actions"
        css={css`
          background: none;
          border: 1px solid ${theme.colors.border};
          border-radius: 4px;
          color: ${theme.colors.text};
          cursor: pointer;
          padding: 2px ${spacing.sm};
          font-size: ${fontSizes.md};
          line-height: 1;
          min-width: 32px;
          min-height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          &:hover {
            background: ${theme.colors.surface};
          }
        `}
      >
        &#8943;
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          css={css`
            background: ${theme.colors.background};
            border: 1px solid ${theme.colors.border};
            border-radius: ${radii.md};
            padding: ${spacing.xs};
            min-width: 160px;
            box-shadow: ${shadows.elevated};
            z-index: 50;
          `}
        >
          <DropdownMenu.Item css={itemCss} onSelect={onChangePlan}>
            Change Plan
          </DropdownMenu.Item>

          {subscriptionStatus !== 'active' && (
            <DropdownMenu.Item css={itemCss} onSelect={() => onChangeStatus('activate')}>
              Activate
            </DropdownMenu.Item>
          )}

          {subscriptionStatus === 'active' && (
            <DropdownMenu.Item css={itemCss} onSelect={() => onChangeStatus('cancel')}>
              Cancel
            </DropdownMenu.Item>
          )}

          {subscriptionStatus !== 'disabled' && (
            <DropdownMenu.Item css={itemCss} onSelect={() => onChangeStatus('disable')}>
              Disable
            </DropdownMenu.Item>
          )}

          <DropdownMenu.Separator
            css={css`
              height: 1px;
              background: ${theme.colors.border};
              margin: ${spacing.xs} 0;
            `}
          />

          <DropdownMenu.Item css={destructiveItemCss} onSelect={onDelete}>
            Delete
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
